import { useMemo, useState } from "react";
import { Trash2, FolderPlus, Search, X, Pencil, Check } from "lucide-react";
import BaseModal from "../BaseComponents/BaseModal/BaseModal";
import DeleteConfirmModal from "../BaseComponents/DeleteConfirmModal/DeleteConfirmModal";
import { useToast } from "../../context/ToastContext";
import {
  useFaqCategoriesList,
  useCreateFaqCategory,
  useUpdateFaqCategory,
  useDeleteFaqCategory,
} from "../../api/cms/useFaqCategoryApi";
import styles from "./FaqCategoriesModal.module.css";

const nameOf = (c) => c?.title?.trim() || `Category #${c?.id}`;

/**
 * Categories grouped the way the FAQ page is built: each main category with its
 * own subcategories beneath it.
 *
 * A subcategory whose parent has been deleted is still listed, on its own and
 * marked, because it is still published and someone has to be able to reach it.
 */
function groupCategories(categories = []) {
  const parents = categories.filter((c) => !c.parentId);
  const childrenOf = (id) => categories.filter((c) => Number(c.parentId) === Number(id));

  const groups = parents.map((parent) => ({
    parent,
    children: childrenOf(parent.id),
    orphan: false,
  }));

  const orphans = categories
    .filter((c) => c.parentId && !categories.some((p) => Number(p.id) === Number(c.parentId)))
    .map((c) => ({ parent: c, children: [], orphan: true }));

  return [...groups, ...orphans];
}

export default function FaqCategoriesModal({ open, onClose, onCategoryCreated, faqs = [] }) {
  const { showToast } = useToast();

  /* How many questions each category holds.

     A subcategory draws nothing on the website until a question is filed under
     it — it is a filter inside its parent's card, and a filter with nothing
     behind it is not worth a button. That is correct, and it is also the first
     thing that looks broken: the dashboard lists the subcategory, the website
     shows no sign of it, and the obvious conclusion is that the site cannot do
     subcategories. SUNSKY reached exactly that conclusion. Saying "0 questions"
     here is what makes the silence legible. */
  const questionsIn = (id) =>
    (faqs || []).filter((f) => Number(f.faqCategoryId) === Number(id)).length;
  const [title, setTitle] = useState("");
  /* The line the website prints under a main category's card, beneath its
     title and above the question count. Optional: a card without one simply
     shows less, which is how several of them look today. */
  const [description, setDescription] = useState("");
  // Empty = a top-level category. Choosing a parent makes the new one a
  // subcategory, e.g. "Booking" inside "Before your trip".
  const [parentId, setParentId] = useState("");
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  // The category currently being edited in place, as { id, title, description }.
  const [editing, setEditing] = useState(null);
  // Filters the list below; it has nothing to do with the form above, which
  // keeps adding wherever the parent picker says.
  const [search, setSearch] = useState("");
  const { data: categories = [], loading, error, refetch } = useFaqCategoriesList();
  const createCategory = useCreateFaqCategory();
  const updateCategory = useUpdateFaqCategory();
  const deleteCategory = useDeleteFaqCategory();

  const groups = useMemo(() => groupCategories(categories || []), [categories]);

  /* Searching keeps the grouping rather than flattening it: a subcategory
     found on its own is meaningless without the main category it sits in, so
     its parent comes along as the heading it belongs to. Matching a main
     category brings its whole group, subcategories included. */
  const q = search.trim().toLowerCase();
  const visibleGroups = useMemo(() => {
    if (!q) return groups;
    const matches = (c) => nameOf(c).toLowerCase().includes(q);
    return groups
      .map((group) => {
        if (matches(group.parent)) return group;
        const children = group.children.filter(matches);
        return children.length ? { ...group, children, contextOnly: true } : null;
      })
      .filter(Boolean);
  }, [groups, q]);

  const shown = visibleGroups.reduce(
    (n, g) => n + (g.contextOnly ? 0 : 1) + g.children.length,
    0
  );
  const parentOptions = useMemo(
    () => (categories || []).filter((c) => !c.parentId),
    [categories]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      await createCategory.execute({
        parentId: parentId ? Number(parentId) : null,
        sortOrder: 0,
        status: "ACTIVE",
        descriptions: [
          { languageCode: "en", title: title.trim(), description: description.trim() || null },
        ],
      });
      setTitle("");
      setDescription("");
      // The parent is deliberately kept: subcategories are added in runs, and
      // re-picking "Before your trip" for each of its four is needless work.
      refetch();
      onCategoryCreated?.();
      showToast("FAQ category created successfully", "success");
    } catch {
      // surfaced through createCategory.error
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editing || !editing.title.trim()) return;
    try {
      /* descriptions replaces the set, so the title goes along with the card
         text even when only the card text was touched. */
      await updateCategory.execute(editing.id, {
        descriptions: [
          {
            languageCode: "en",
            title: editing.title.trim(),
            description: editing.description.trim() || null,
          },
        ],
      });
      setEditing(null);
      refetch();
      onCategoryCreated?.();
      showToast("FAQ category updated successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to update category", "error");
    }
  };

  const handleConfirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    try {
      await deleteCategory.execute(categoryToDelete.id);
      refetch();
      setCategoryToDelete(null);
      onCategoryCreated?.();
      showToast("FAQ category deleted successfully", "success");
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to delete category", "error");
      throw err;
    }
  };

  const total = categories?.length || 0;
  const parentName = parentOptions.find((c) => String(c.id) === String(parentId));

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title="FAQ Categories"
      width="620px"
      footer={
        <button type="button" className={styles.closeBtn} onClick={onClose}>
          Close
        </button>
      }
    >
      {/* The title gets the full width of its own row. It used to share a
          480px row with the parent picker and the button, which squeezed its
          label into three wrapped lines and its input to about 150px. */}
      <form onSubmit={handleCreate} className={styles.addPanel}>
        <p className={styles.addTitle}>Add a category</p>

        <div className={styles.field}>
          <label htmlFor="faq-cat-title">Category title</label>
          <input
            id="faq-cat-title"
            placeholder="e.g. Voor de reis, Betalingen"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Only main categories draw a card on the website, so only they have
            anywhere to print this. */}
        {!parentId && (
          <div className={styles.field}>
            <label htmlFor="faq-cat-description">
              Card text <span className={styles.optional}>optional</span>
            </label>
            <textarea
              id="faq-cat-description"
              rows={2}
              placeholder="e.g. Je bevestiging, betalingen, wijzigingen, reisdocumenten en inchecken."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className={styles.fieldHint}>
              One or two lines under the title on the FAQ card. Leave it empty and the card just
              shows its title and question count.
            </p>
          </div>
        )}

        <div className={styles.addRow}>
          <div className={styles.field}>
            <label htmlFor="faq-cat-parent">Place inside</label>
            {/* Only top-level categories are offered: nesting is one level
                deep, which is what the FAQ page can draw (cards, with their
                subcategories as filters inside the open card). */}
            <select
              id="faq-cat-parent"
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
            >
              <option value="">Nothing (a main category)</option>
              {parentOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {nameOf(c)}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className={styles.addBtn}
            disabled={createCategory.loading || !title.trim()}
          >
            <FolderPlus size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />
            {createCategory.loading
              ? "Adding…"
              : parentId
                ? "Add subcategory"
                : "Add category"}
          </button>
        </div>

        {/* Says where the new one will land, so the parent staying selected
            between additions is a visible choice rather than a surprise. */}
        <p className={styles.hint}>
          {parentName
            ? `This will be added inside “${nameOf(parentName)}”.`
            : "This will be a main category. Choose a parent above to make it a subcategory instead."}
        </p>

        {createCategory.error && <p className={styles.errorText}>{createCategory.error}</p>}
      </form>

      {/* Sticky, so the search box and the count stay put while a long list
          scrolls under them. */}
      <div className={styles.listHead}>
        <div className={styles.listHeadTop}>
          <span className={styles.listTitle}>Current categories</span>
          {total > 0 && (
            <span className={styles.count}>
              {q
                ? `${shown} of ${total} shown`
                : `${total} ${total === 1 ? "category" : "categories"}`}
            </span>
          )}
        </div>

        {total > 0 && (
          <div className={styles.searchBar}>
            <Search size={14} className={styles.searchIcon} />
            <input
              type="search"
              className={styles.searchInput}
              placeholder="Search categories and subcategories…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search categories"
            />
            {search && (
              <button
                type="button"
                className={styles.clearSearch}
                onClick={() => setSearch("")}
                title="Clear search"
                aria-label="Clear search"
              >
                <X size={13} />
              </button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : error ? (
        <p className={styles.errorText}>{error}</p>
      ) : !total ? (
        <p className={styles.empty}>No categories yet. Add the first one above.</p>
      ) : !visibleGroups.length ? (
        <p className={styles.empty}>
          No categories match “{search.trim()}”.{" "}
          <button type="button" className={styles.linkBtn} onClick={() => setSearch("")}>
            Clear the search
          </button>
        </p>
      ) : (
        <div className={styles.list}>
          {visibleGroups.map(({ parent, children, orphan, contextOnly }) => (
            <div key={parent.id} className={styles.group}>
              {editing?.id === parent.id ? (
                <form
                  className={styles.editRow}
                  onSubmit={handleSaveEdit}
                  aria-label={`Edit ${nameOf(parent)}`}
                >
                  <div className={styles.field}>
                    <label htmlFor="faq-cat-edit-title">Category title</label>
                    <input
                      id="faq-cat-edit-title"
                      autoFocus
                      value={editing.title}
                      onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    />
                  </div>
                  <div className={styles.field}>
                    <label htmlFor="faq-cat-edit-description">
                      Card text <span className={styles.optional}>optional</span>
                    </label>
                    <textarea
                      id="faq-cat-edit-description"
                      rows={2}
                      placeholder="The line printed under this card on the FAQ page"
                      value={editing.description}
                      onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                    />
                  </div>
                  <div className={styles.editActions}>
                    <button
                      type="button"
                      className={styles.editCancel}
                      onClick={() => setEditing(null)}
                      disabled={updateCategory.loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={styles.editSave}
                      disabled={updateCategory.loading || !editing.title.trim()}
                    >
                      <Check size={13} />
                      {updateCategory.loading ? "Saving…" : "Save"}
                    </button>
                  </div>
                </form>
              ) : (
              <div
                className={`${styles.row} ${styles.rowParent} ${
                  contextOnly ? styles.rowContext : ""
                }`}
              >
                <span className={styles.bullet} />
                <span className={styles.nameBlock}>
                  <span className={styles.name}>{nameOf(parent)}</span>
                  {/* Shown as it will read on the card, or as the gap it
                      currently is — that gap is the thing people notice on the
                      site and cannot otherwise explain. */}
                  {parent.description ? (
                    <span className={styles.cardText}>{parent.description}</span>
                  ) : (
                    <span className={styles.cardTextEmpty}>no card text</span>
                  )}
                </span>
                {orphan && <span className={styles.orphan}>parent deleted</span>}
                {!orphan && children.length > 0 && (
                  <span className={styles.subCount}>
                    {children.length} sub
                  </span>
                )}
                <span className={styles.qCount}>
                  {questionsIn(parent.id)} {questionsIn(parent.id) === 1 ? "question" : "questions"}
                </span>
                <button
                  type="button"
                  title={`Edit ${nameOf(parent)}`}
                  aria-label={`Edit ${nameOf(parent)}`}
                  className={styles.editBtn}
                  onClick={() =>
                    setEditing({
                      id: parent.id,
                      title: parent.title || "",
                      description: parent.description || "",
                    })
                  }
                >
                  <Pencil size={14} />
                </button>
                <button
                  type="button"
                  title={`Delete ${nameOf(parent)}`}
                  aria-label={`Delete ${nameOf(parent)}`}
                  className={styles.deleteBtn}
                  onClick={() => setCategoryToDelete(parent)}
                >
                  <Trash2 size={14} />
                </button>
              </div>
              )}

              {children.map((child) => (
                <div key={child.id} className={`${styles.row} ${styles.rowChild}`}>
                  <span className={`${styles.bullet} ${styles.bulletChild}`} />
                  <span className={styles.name}>{nameOf(child)}</span>
                  {questionsIn(child.id) === 0 ? (
                    <span className={styles.qCountEmpty}>
                      no questions yet, so it stays hidden on the website
                    </span>
                  ) : (
                    <span className={styles.qCount}>
                      {questionsIn(child.id)} {questionsIn(child.id) === 1 ? "question" : "questions"}
                    </span>
                  )}
                  <button
                    type="button"
                    title={`Delete ${nameOf(child)}`}
                    aria-label={`Delete ${nameOf(child)}`}
                    className={styles.deleteBtn}
                    onClick={() => setCategoryToDelete(child)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <DeleteConfirmModal
        open={!!categoryToDelete}
        onClose={() => setCategoryToDelete(null)}
        title="Delete FAQ category"
        message={
          categoryToDelete?.title?.trim()
            ? undefined
            : "Are you sure you want to delete this category? This action cannot be undone."
        }
        itemName={categoryToDelete?.title?.trim() || undefined}
        onConfirm={handleConfirmDeleteCategory}
        loading={deleteCategory.loading}
      />
    </BaseModal>
  );
}
